import { useEffect, useState } from 'react';
import { autoBiddingApi } from '@/api/client';
import { AutoBiddingModal } from '@/components/workspace/AutoBiddingModal';
import {
  parseAutoBiddingSettings,
  supportsAutoBidding,
  apiErrorMessage,
  type AutoBiddingSavePayload,
  type AutoBiddingSettings,
} from '@/utils/autoBidding';

export function WorkspaceAutoBiddingModal({
  profileId,
  campaignId,
  campaignName,
  adProduct,
  onClose,
  onChanged,
}: {
  profileId: string;
  campaignId: string;
  campaignName: string;
  adProduct: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [initial, setInitial] = useState<AutoBiddingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const product = supportsAutoBidding(adProduct) ? (adProduct as 'SP' | 'SB') : null;

  useEffect(() => {
    let cancelled = false;
    if (!product) {
      setLoading(false);
      setError('Auto bidding is available for SP and SB');
      return;
    }
    setLoading(true);
    setError(null);
    void autoBiddingApi
      .get(profileId, campaignId, product)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.message || 'Failed to load auto bidding');
          return;
        }
        setInitial(parseAutoBiddingSettings(res.data));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(apiErrorMessage(err, 'Failed to load auto bidding'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId, campaignId, product]);

  async function handleSave(payload: AutoBiddingSavePayload) {
    if (!product) return;
    setSaving(true);
    setError(null);
    try {
      const res = await autoBiddingApi.save(profileId, campaignId, product, payload);
      if (!res.success) {
        setError(res.message || 'Failed to save auto bidding');
        return;
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save auto bidding'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDisable() {
    if (!product) return;
    setDisabling(true);
    setError(null);
    try {
      const res = await autoBiddingApi.disable(profileId, campaignId, product);
      if (!res.success) {
        setError(res.message || 'Failed to disable auto bidding');
        return;
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to disable auto bidding'));
    } finally {
      setDisabling(false);
    }
  }

  return (
    <AutoBiddingModal
      campaignName={campaignName}
      initial={initial}
      loading={loading}
      saving={saving}
      disabling={disabling}
      error={error}
      onClose={onClose}
      onSave={(payload) => void handleSave(payload)}
      onDisable={initial?.enabled ? () => void handleDisable() : undefined}
    />
  );
}
